'use client';

import { useEffect, useState } from 'react';

interface UseRecaptchaReturn {
    executeRecaptcha: (action: string) => Promise<string | null>;
    isLoaded: boolean;
    error: string | null;
}

declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

export function useRecaptcha(): UseRecaptchaReturn {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY;

        if (!siteKey) {
            setError('reCAPTCHA site key not configured');
            return;
        }

        // Check if script is already loaded
        if (window.grecaptcha) {
            setIsLoaded(true);
            return;
        }

        // Load reCAPTCHA v3 script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.grecaptcha) {
                window.grecaptcha.ready(() => {
                    setIsLoaded(true);
                    // Hide the reCAPTCHA badge by removing inline styles
                    const badge = document.querySelector('.grecaptcha-badge') as HTMLElement;
                    if (badge) {
                        badge.style.visibility = 'hidden';
                        badge.style.opacity = '0';
                    }
                });
            }
        };

        script.onerror = () => {
            setError('Failed to load reCAPTCHA script');
        };

        document.head.appendChild(script);

        // Cleanup
        return () => {
            // Note: We don't remove the script on unmount as it might be used by other components
        };
    }, []);

    const executeRecaptcha = async (action: string): Promise<string | null> => {
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY;

        if (!siteKey) {
            console.error('reCAPTCHA site key not configured');
            return null;
        }

        if (!isLoaded || !window.grecaptcha) {
            console.error('reCAPTCHA not loaded');
            return null;
        }

        try {
            const token = await window.grecaptcha.execute(siteKey, { action });
            return token;
        } catch (err) {
            console.error('Error executing reCAPTCHA:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
            return null;
        }
    };

    return {
        executeRecaptcha,
        isLoaded,
        error,
    };
}
